/* Экран «Рилс с вами» и «Вы в кино» по опубликованной форме (APP-SPEC-5 §8; 03-api §6.3, §6.6, §9.2).
   Источники — только сервер: поля формы (`fields`), её политика `constraints.reels` и запись шаблона
   (`GET /app/studio/apps/{id}`). Контрол встаёт на экран, когда форма публикует его поле:
   - `cast_<role_id>` (type `cast`) — исполнители роли, ссылки через запятую (self, user_avatar:ID, person:ID, character:ID);
   - фото предмета — upload `photo` с ключами `<вид>_<N>` (`goods_1`…, `food_1`…, `place_1`…, `pics_1`…, `photo`…);
   - `template_id` / `template_ids` (values — допустимые шаблоны) и `mode` (`one` · `multi` · `pack` · `app`);
   - `text` — сценарий прежнего воркфлоу. */
import { uiFormat } from '../../i18n';
import type { StudioApp, StudioForm } from '../studio/studio-api';
import { mediaUrl, record, rows, text } from '../studio/studio-content';
import { defaultRole, JSON_CAST, normalizeRole, type ReelsRole } from './reels-cast';
import { keyInfo, screenFields } from './form-keys';

export type ReelsMode = 'one' | 'multi' | 'pack' | 'app';
export type ReelsField = StudioForm['fields'][number];
export interface ReelsTemplate {
  id: string;
  title: string;
  plot: string;
  need: string;
  categoryId: string;
  durationS: number | null;
  aspect: string;
  type: string;
  price: number | null;
  morePrice: number | null;
  badge: 'hit' | 'new' | null;
  overlay: string;
  /** Текст накладки обложки (overlay.text: дата плёнки, «Глава 1»…); нет — '' */
  overlayText: string;
  frames: string[];
  videoUrl: string | null;
  cast: unknown[] | null;
  photos: { min: number; max: number } | null;
  pack?: boolean;
}
export interface ReelsObjectNeed {
  kind: string;
  label: string;
  of: string;
  tip: string;
  min: number;
  max: number;
  slots: ReelsField[];
}
export interface ReelsPolicy {
  mode: ReelsMode | null;
  scene: boolean;
  variants: number | null;
  categories: Array<{ id: string; title: string }>;
  templates: ReelsTemplate[];
  types: Record<string, { title: string; price: number | null; morePrice: number | null }>;
  needs: Record<string, { label: string; people: boolean; of: string; tip: string }>;
  castByNeed: Record<string, unknown[]>;
  cast: unknown[] | null;
  multi: { min: number; discountFrom: number; discountPct: number } | null;
  pack: { id: string; title: string; count: number; price: number | null; comparePrice: number | null; durationS: number | null; badge: 'hit' | 'new' | null } | null;
  morePrice: number | null;
  /** Сумма дозаказа, которую сверяет сервер (constraints.reels.more.expected_price, прайс); цены типов и шаблонов — оформление каталога */
  serverMorePrice: number | null;
  moreAvailable: boolean;
  maxObjectPhotos: number;
  template: ReelsTemplate | null;
}

const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null);
const badge = (value: unknown) => (value === 'hit' || value === 'Хит' ? 'hit' : value === 'new' || value === 'Новое' ? 'new' : null);
const urls = (value: unknown) => (Array.isArray(value) ? value.map(mediaUrl).filter((x): x is string => !!x) : []);

export function parseTemplate(raw: unknown): ReelsTemplate | null {
  const t = record(raw);
  const id = typeof t.id === 'number' ? String(t.id) : text(t.id);
  if (!/^[a-zA-Z0-9_-]{1,64}$/.test(id)) return null;
  const example = record(t.example);
  const preview = record(t.preview);
  const photos = record(t.photos);
  const frames = [...urls(example.frames), ...urls(t.frames)];
  const coverUrl = mediaUrl(t.cover_url) ?? mediaUrl(preview.after_url);
  return {
    id,
    title: text(t.title) || text(t.name) || id,
    plot: text(t.plot) || text(t.sub) || text(t.description),
    need: text(t.need),
    categoryId: text(t.category_id),
    durationS: num(t.duration_s),
    aspect: text(t.aspect) || '9:16',
    type: text(t.type),
    price: num(t.price),
    morePrice: num(t.more_price),
    badge: badge(t.badge),
    overlay: text(record(t.overlay).kind),
    overlayText: text(record(t.overlay).text),
    frames: frames.length ? frames : coverUrl ? [coverUrl] : [],
    videoUrl: mediaUrl(example.video_url),
    cast: Array.isArray(t.cast) && t.cast.length ? t.cast : null,
    photos: Number.isInteger(photos.min) && Number.isInteger(photos.max) ? { min: photos.min as number, max: photos.max as number } : null,
  };
}

/** Словарь видов предмета — подписи интерфейса из спецификации (§8.2 «Фото предмета»); сервер может их заменить. */
export const objectWords: Record<string, { label: string; of: string; tip: string }> = {
  goods: { label: 'Фото товара', of: 'товара', tip: 'Товар целиком, на чистом фоне, без надписей' },
  food: { label: 'Фото блюда', of: 'блюда', tip: 'Блюдо крупно, при дневном свете' },
  place: { label: 'Фото места', of: 'места', tip: 'Светлые кадры, каждое место целиком' },
  pics: { label: 'Ваши фото', of: '', tip: 'Чёткие снимки: лица и детали хорошо видно' },
};

export function reelsPolicy(form: StudioForm, app?: StudioApp): ReelsPolicy {
  const p = record(record(form.constraints).reels);
  const version = text(p.policy_version);
  const usable = !version || version.startsWith('reels-');
  const src = usable ? p : {};
  const offer = record(src.offer);
  const multi = record(offer.multi ?? src.multi);
  const pack = record(offer.pack ?? src.pack);
  const types: ReelsPolicy['types'] = {};
  for (const t of rows(src.types)) {
    const key = text(t.key);
    if (key) types[key] = { title: text(t.title), price: num(t.price), morePrice: num(t.more_price) };
  }
  /* цена дозаказа по типу из more.by_type — там, где у типа своей more_price нет */
  const moreByType = rows(record(src.more).by_type).filter(x => text(x.type) && num(x.price) !== null);
  for (const x of moreByType) {
    const t = types[text(x.type)];
    if (t && t.morePrice === null) t.morePrice = num(x.price);
  }
  const needs: ReelsPolicy['needs'] = {};
  for (const n of rows(src.needs)) {
    const key = text(n.key);
    const object = record(n.object);
    if (key) needs[key] = { label: text(n.label), people: n.people === true, of: text(object.of), tip: text(object.tip) };
  }
  const templates = rows(src.templates).map(parseTemplate).filter((x): x is ReelsTemplate => !!x);
  const appRecord = record(app);
  const own = parseTemplate(Object.keys(record(src.template)).length ? src.template : null);
  const appTemplate = app
    ? parseTemplate({
        ...appRecord,
        id: String(app.id),
        title: appRecord.name,
        cover_url: appRecord.cover_url,
        preview: appRecord.preview,
      })
    : null;
  // Роли открытого шаблона: `constraints.reels.app_cast` формы (docs/59 §5b), иначе `cast` карточки шаблона (03-api §6.3).
  const appCast =
    Array.isArray(src.app_cast) && src.app_cast.length ? src.app_cast : Array.isArray(appRecord.cast) && appRecord.cast.length ? appRecord.cast : null;
  // Режим: ключа `mode` в constraints.reels контракта нет (там `modes`) — старое имя читается для совместимости;
  // открытый шаблон с ролями — сцена «Вы в кино» (режим `app`, у эталона — S.app).
  const declared = ['one', 'multi', 'pack', 'app'].includes(text(src.mode)) ? (text(src.mode) as ReelsMode) : null;
  const modes = Array.isArray(src.modes) ? src.modes.filter((x): x is string => typeof x === 'string') : null;
  const mode = declared ?? (appCast && (!modes || modes.includes('app')) ? 'app' : null);
  const castByNeed: Record<string, unknown[]> = {};
  for (const [key, value] of Object.entries(record(src.cast_by_need))) if (Array.isArray(value)) castByNeed[key] = value;
  return {
    mode,
    scene: src.scene === true || (mode === 'app' && !!appCast && src.scene !== false),
    variants: num(offer.variants ?? src.variants),
    categories: rows(src.categories).flatMap(c => (text(c.id) ? [{ id: text(c.id), title: text(c.title) || text(c.id) }] : [])),
    templates,
    types,
    needs,
    castByNeed,
    cast: Array.isArray(src.cast) && src.cast.length ? src.cast : appCast,
    multi:
      Object.keys(multi).length
        ? { min: num(multi.min_templates) ?? 2, discountFrom: num(multi.discount_from) ?? Infinity, discountPct: num(multi.discount_pct) ?? 0 }
        : null,
    pack: Object.keys(pack).length
      ? {
          id: text(pack.id) || 'pack',
          title: text(pack.title) || uiFormat('{0} рилсов сразу', num(pack.count) ?? 10),
          count: num(pack.count) ?? 10,
          price: num(pack.price),
          comparePrice: num(pack.compare_price),
          durationS: num(pack.duration_s),
          badge: badge(pack.badge),
        }
      : null,
    morePrice: num(record(src.more).price ?? src.more_price),
    serverMorePrice: num(record(src.more).expected_price),
    // docs/59 §5b: `more` = {variants, by_type: [{type, price}]} (без `types` — null); старые формы — available / more_price
    moreAvailable: record(src.more).available === true || num(src.more_price) !== null || moreByType.length > 0,
    maxObjectPhotos: Math.min(20, num(offer.max_object_photos) ?? 20),
    template: own ?? (mode === 'app' || !templates.length ? appTemplate : null),
  };
}

const castKey = /^cast_([a-zA-Z][a-zA-Z0-9_]{0,31})$/;
const consentKey = /^consent_(accepted|version)$/;
const selectionKeys = new Set(['template_id', 'template_ids', 'mode']);
/** Ключи «Рилса» в контракте экрана (03-api §9.2) */
const reelsScreenKeys = new Set(['mode', 'template_id', 'template_ids', 'cast', 'objects']);

/** Форма, которую экран умеет показать целиком; незнакомое поле — экран не выдаёт себя за совместимый. */
export function reelsSupported(form: StudioForm): boolean {
  return (
    form.screen === 'reels' &&
    form.fields.length + screenFields(form).length > 0 &&
    // Ключ контракта экрана, которого экран не знает, допустим только необязательным.
    screenFields(form).every(f => reelsScreenKeys.has(f.key) || !f.required) &&
    form.fields.every(
      field =>
        (field.key === 'text' && field.type === 'text' && !field.upload && !field.values.length) ||
        field.upload?.kind === 'photo' ||
        (castKey.test(field.key) && field.type === 'cast' && !field.upload) ||
        (selectionKeys.has(field.key) && !field.upload) ||
        consentKey.test(field.key),
    )
  );
}

export function objectKind(key: string): string {
  const base = key.replace(/_\d+$/, '');
  return base === 'photo' || base === 'photos' ? 'pics' : base;
}
/** Лотки фото предмета: поля одного вида — слоты одного лотка, порядок — порядок полей. */
export function objectNeeds(form: StudioForm, policy: ReelsPolicy, templates: ReelsTemplate[]): ReelsObjectNeed[] {
  const groups = new Map<string, ReelsField[]>();
  for (const field of form.fields)
    if (field.upload?.kind === 'photo') {
      const kind = objectKind(field.key);
      groups.set(kind, [...(groups.get(kind) ?? []), field]);
    }
  return [...groups.entries()].map(([kind, slots]) => {
    const words = { ...objectWords.pics, ...objectWords[kind], ...Object.fromEntries(Object.entries(policy.needs[kind] ?? {}).filter(([, v]) => !!v)) };
    // Набор: столько, сколько требует самый строгий шаблон вида, и не больше наибольшего предела.
    const limits = templates.filter(t => t.photos && (objectKind(t.need) === kind || t.need === kind)).map(t => t.photos!);
    const required = slots.filter(slot => slot.required).length;
    const max = Math.min(slots.length, policy.maxObjectPhotos, limits.length ? Math.max(...limits.map(l => l.max)) : slots.length);
    const min = Math.min(max, Math.max(required, limits.length ? Math.max(...limits.map(l => l.min)) : 0));
    return { kind, label: words.label, of: words.of, tip: words.tip, min, max, slots };
  });
}

/** Роли экрана: роли шаблона (или «по виду» и одна «Главный герой»), у которых форма публикует поле. */
export function screenRoles(form: StudioForm, policy: ReelsPolicy, mode: ReelsMode, templates: ReelsTemplate[]): { roles: ReelsRole[]; orphan: string | null } {
  const fields = form.fields.filter(field => castKey.test(field.key) && field.type === 'cast');
  // JSON-ключ `cast` контракта экрана: один ключ на все роли (03-api §9.2); строковые `cast_<роль>` шаблона — по ключу на роль.
  const whole = !fields.length ? keyInfo(form, JSON_CAST) : null;
  if (!fields.length && !whole) return { roles: [], orphan: null };
  const fieldOf = (id: string): { key: string; required: boolean } | undefined =>
    whole ? { key: JSON_CAST, required: true } : fields.find(field => field.key === `cast_${id}`);
  const fromTemplate = (t: ReelsTemplate | null) => t?.cast ?? (t?.need ? (policy.castByNeed[t.need] ?? null) : null);
  const declared: Array<{ raw: unknown; template: string }> =
    mode === 'multi' ? templates.flatMap(t => (fromTemplate(t) ?? []).map(raw => ({ raw, template: t.id }))) : mode === 'pack' ? (policy.castByNeed.me ?? []).map(raw => ({ raw, template: 'pack' })) : (fromTemplate(templates[0] ?? null) ?? policy.cast ?? []).map(raw => ({ raw, template: templates[0]?.id ?? '' }));
  const roles: ReelsRole[] = [];
  let orphan: string | null = null;
  for (const { raw, template } of declared) {
    const id = text(record(raw).id);
    const field = fieldOf(id);
    const role = field ? normalizeRole(raw, field.key) : null;
    if (!role) {
      if (!field && (record(raw).min ?? 1) !== 0 && record(raw).required !== false) orphan = text(record(raw).name) || id;
      continue;
    }
    const known = roles.find(r => r.id === role.id);
    // Одинаковая роль у двух шаблонов набора — один слот на все шаблоны (решение по умолчанию 07.10.2026).
    if (known) known.templates = [...new Set([...known.templates, template])];
    else roles.push({ ...role, templates: mode === 'multi' ? [template] : [], min: field?.required === false ? 0 : role.min });
  }
  if (!declared.length && whole) roles.push(defaultRole(JSON_CAST));
  if (!declared.length) for (const field of fields) roles.push({ ...defaultRole(field.key, field.key.slice(5)), min: field.required ? 1 : 0 });
  return { roles, orphan };
}

/** Цена набора: сумма цен шаблонов, от `discountFrom` шаблонов — скидка; без цены шаблона — неизвестна. */
export function multiPrice(policy: ReelsPolicy, templates: ReelsTemplate[]): number | null {
  const prices = templates.map(t => t.price ?? policy.types[t.type]?.price ?? null);
  if (!prices.length || prices.some(p => p === null)) return null;
  const sum = (prices as number[]).reduce((a, b) => a + b, 0);
  const off = policy.multi && templates.length >= policy.multi.discountFrom ? policy.multi.discountPct : 0;
  return Math.round((sum * (100 - off)) / 100);
}

export const plural = (n: number, one: string, few: string, many: string) => {
  const a = Math.abs(n) % 100,
    b = a % 10;
  return a > 10 && a < 20 ? many : b > 1 && b < 5 ? few : b === 1 ? one : many;
};
export const mmss = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
export const aspectWord = (aspect: string) =>
  ({ '9:16': 'Сторис', '1:1': 'Квадрат', '4:5': 'Пост', '3:4': 'Портрет', '16:9': 'Горизонтальное' })[aspect] ?? aspect;
